import { ixAddToCart } from '@theme/ix-cart';

/**
 * Magnetic Ingredients Section
 * Interactive ingredient discovery with compatibility physics
 */

class MagneticIngredients {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.debug = false;

    // DOM Elements
    this.grid = section.querySelector('[data-grid]');
    this.center = section.querySelector('[data-center]');
    this.centerText = this.center?.querySelector('.magnetic-ingredients__center-text');
    this.particlesContainer = section.querySelector('[data-particles]');
    this.productCard = section.querySelector('[data-product-card]');
    this.productInner = section.querySelector('[data-product-inner]');
    this.clearBtn = section.querySelector('[data-clear]');
    this.selectedItemsContainer = section.querySelector('[data-selected-items]');
    this.noteContainer = section.querySelector('[data-note]');
    this.orbs = section.querySelectorAll('[data-orb]');
    this.bondsSvg = section.querySelector('[data-bonds]');
    this.liquid = section.querySelector('[data-liquid]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Data
    this.productMatches = this.loadData('[data-product-matches]');
    this.compatibilityNotes = this.loadData('[data-compatibility-notes]');

    // State
    this.selectedIngredients = [];
    this.repellingOrb = null;
    this.initialCenterText = this.centerText?.textContent || 'Select 2 ingredients';

    this.init();
  }

  log(...args) {
    if (this.debug) console.log(...args);
  }

  loadData(selector) {
    const script = this.section.querySelector(selector);
    if (!script) {
      if (this.debug) console.warn('Data script not found:', selector);
      return [];
    }
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      if (this.debug) console.error('Failed to parse data:', selector, e);
      return [];
    }
  }

  init() {
    this.bindEvents();
    this.updateOrbPositions();
    this.initMagnet();
  }

  /* ------------------------------------------------------------------ */
  /* Magnetic pointer attraction: orbs drift toward the cursor within a  */
  /* radius. Runs only for fine pointers, pauses off-screen, and stays   */
  /* out of the way of selected/merging orbs (their transform is owned   */
  /* by the merge animation).                                            */
  /* ------------------------------------------------------------------ */
  initMagnet() {
    if (!this.finePointer || this.reducedMotion || !this.grid) return;

    this.magnetPointer = null;
    this.magnetStates = Array.from(this.orbs, () => ({ x: 0, y: 0 }));
    let running = false;

    const tick = () => {
      let settled = true;
      const gridRect = this.grid.getBoundingClientRect();
      this.orbs.forEach((orb, i) => {
        const state = this.magnetStates[i];
        let targetX = 0;
        let targetY = 0;
        if (
          this.magnetPointer &&
          !orb.classList.contains('is-selected') &&
          !orb.classList.contains('is-merging') &&
          !orb.classList.contains('is-hidden') &&
          orb.dataset.originalLeft
        ) {
          const baseX = gridRect.left + (parseFloat(orb.dataset.originalLeft) / 100) * gridRect.width;
          const baseY = gridRect.top + (parseFloat(orb.dataset.originalTop) / 100) * gridRect.height;
          const dx = this.magnetPointer.x - baseX;
          const dy = this.magnetPointer.y - baseY;
          const dist = Math.hypot(dx, dy);
          const reach = 200;
          if (dist < reach && dist > 1) {
            const pull = (1 - dist / reach) * 22;
            targetX = (dx / dist) * pull;
            targetY = (dy / dist) * pull;
          }
        }
        state.x += (targetX - state.x) * 0.12;
        state.y += (targetY - state.y) * 0.12;
        if (Math.abs(state.x) > 0.1 || Math.abs(state.y) > 0.1 || targetX || targetY) settled = false;
        orb.style.setProperty('--mag-x', `${state.x.toFixed(2)}px`);
        orb.style.setProperty('--mag-y', `${state.y.toFixed(2)}px`);
      });
      if (settled && !this.magnetPointer) {
        running = false;
        return;
      }
      requestAnimationFrame(tick);
    };

    const start = () => {
      if (!running) {
        running = true;
        requestAnimationFrame(tick);
      }
    };

    this.grid.addEventListener('pointermove', (e) => {
      this.magnetPointer = { x: e.clientX, y: e.clientY };
      start();
    });
    this.grid.addEventListener('pointerleave', () => {
      this.magnetPointer = null;
      start();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Molecular bond lines between selected orbs and the mixing core.     */
  /* ------------------------------------------------------------------ */
  renderBonds() {
    if (!this.bondsSvg) return;
    const gridRect = this.grid.getBoundingClientRect();
    this.bondsSvg.setAttribute('viewBox', `0 0 ${Math.round(gridRect.width)} ${Math.round(gridRect.height)}`);
    this.bondsSvg.innerHTML = '';

    this.selectedIngredients.forEach((ing) => {
      const orb = this.section.querySelector(`[data-ingredient-id="${ing.id}"]`);
      if (!orb || orb.classList.contains('is-hidden')) return;
      // anchor to the orbit slot so the line stays put while the orb floats
      let x1;
      let y1;
      if (orb.dataset.originalLeft) {
        x1 = (parseFloat(orb.dataset.originalLeft) / 100) * gridRect.width;
        y1 = (parseFloat(orb.dataset.originalTop) / 100) * gridRect.height;
      } else {
        const orbRect = orb.getBoundingClientRect();
        x1 = orbRect.left + orbRect.width / 2 - gridRect.left;
        y1 = orbRect.top + orbRect.height / 2 - gridRect.top;
      }
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', x1.toFixed(1));
      line.setAttribute('y1', y1.toFixed(1));
      line.setAttribute('x2', (gridRect.width / 2).toFixed(1));
      line.setAttribute('y2', (gridRect.height / 2).toFixed(1));
      line.setAttribute('class', 'mi-bond');
      line.style.stroke = ing.color || 'currentColor';
      this.bondsSvg.appendChild(line);
    });
  }

  flashWarnBond(orbA, orbB) {
    if (!this.bondsSvg || !orbA || !orbB) return;
    const gridRect = this.grid.getBoundingClientRect();
    const a = orbA.getBoundingClientRect();
    const b = orbB.getBoundingClientRect();
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', (a.left + a.width / 2 - gridRect.left).toFixed(1));
    line.setAttribute('y1', (a.top + a.height / 2 - gridRect.top).toFixed(1));
    line.setAttribute('x2', (b.left + b.width / 2 - gridRect.left).toFixed(1));
    line.setAttribute('y2', (b.top + b.height / 2 - gridRect.top).toFixed(1));
    line.setAttribute('class', 'mi-bond mi-bond--warn');
    this.bondsSvg.appendChild(line);
    setTimeout(() => line.remove(), 700);
  }

  /* ------------------------------------------------------------------ */
  /* Mixing core liquid: fills with a blend of the selected ingredient   */
  /* colors (one color = half full, two = nearly full).                  */
  /* ------------------------------------------------------------------ */
  updateFlask() {
    if (!this.liquid) return;
    const count = this.selectedIngredients.length;
    const colorA = this.selectedIngredients[0]?.color || 'transparent';
    const colorB = this.selectedIngredients[1]?.color || colorA;
    this.center.style.setProperty('--mix-a', colorA);
    this.center.style.setProperty('--mix-b', colorB);
    this.center.style.setProperty('--mix-level', count === 0 ? '0' : count === 1 ? '0.45' : '0.85');
    this.center.classList.toggle('has-liquid', count > 0);
  }

  bindEvents() {
    // Orb clicks
    this.orbs.forEach(orb => {
      orb.addEventListener('click', () => this.handleOrbClick(orb));
    });

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearSelection());
    }

    // Add to cart from product card + close button (delegated)
    this.section.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        this.handleAddToCart(e.target);
      }
      if (e.target.matches('[data-close-product]')) {
        this.clearSelection();
      }
    });

    // Keyboard: Escape to dismiss product card
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.productCard?.classList.contains('is-visible')) {
        this.clearSelection();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      this.updateOrbPositions();
      this.renderBonds();
    });

    // Swipe-to-dismiss on mobile
    this.initSwipeToDismiss();

    // Warm the match product images on first intent so the card opens
    // with its photo already cached
    const warm = () => this.preloadMatchImages();
    this.grid?.addEventListener('pointerenter', warm, { once: true });
    this.grid?.addEventListener('touchstart', warm, { once: true, passive: true });
    this.grid?.addEventListener('focusin', warm, { once: true });
  }

  initSwipeToDismiss() {
    let startY = 0;
    let currentY = 0;
    const inner = this.productInner;
    if (!inner) return;

    inner.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      currentY = startY;
    }, { passive: true });

    inner.addEventListener('touchmove', (e) => {
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;
      if (diff > 0) {
        inner.style.transform = `translateY(${diff}px)`;
        inner.style.opacity = Math.max(0, 1 - diff / 200);
      }
    }, { passive: true });

    inner.addEventListener('touchend', () => {
      const diff = currentY - startY;
      if (diff > 80) {
        this.clearSelection();
      }
      inner.style.transform = '';
      inner.style.opacity = '';
      startY = 0;
      currentY = 0;
    });
  }

  preloadMatchImages() {
    if (this.imagesWarmed) return;
    this.imagesWarmed = true;
    this.productMatches.forEach((match) => {
      if (match.product?.image) {
        new Image().src = match.product.image;
      }
    });
  }

  updateOrbPositions() {
    this.orbs.forEach(orb => {
      const rect = orb.getBoundingClientRect();
      const gridRect = this.grid.getBoundingClientRect();
      orb.dataset.originalLeft = ((rect.left - gridRect.left + rect.width / 2) / gridRect.width * 100).toFixed(2);
      orb.dataset.originalTop = ((rect.top - gridRect.top + rect.height / 2) / gridRect.height * 100).toFixed(2);
    });
  }

  handleOrbClick(orb) {
    const ingredientId = orb.dataset.ingredientId;
    const ingredientName = orb.dataset.ingredientName;

    this.log('Orb clicked:', ingredientName);

    // If already selected, deselect
    if (this.selectedIngredients.some(i => i.id === ingredientId)) {
      this.deselectIngredient(ingredientId);
      return;
    }

    // Check compatibility with last selected
    if (this.selectedIngredients.length > 0) {
      const lastSelected = this.selectedIngredients[this.selectedIngredients.length - 1];
      const lastOrb = this.section.querySelector(`[data-ingredient-id="${lastSelected.id}"]`);

      if (this.isIncompatible(lastOrb, orb)) {
        this.triggerRepel(orb);
        this.flashWarnBond(lastOrb, orb);
        this.showIncompatibilityNote(lastSelected.name, ingredientName);
        return;
      }
    }

    // Select the ingredient
    this.selectIngredient(orb, ingredientId, ingredientName);
  }

  isIncompatible(orbA, orbB) {
    const incompatibleA = (orbA.dataset.incompatible || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
    const incompatibleB = (orbB.dataset.incompatible || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
    const nameA = orbA.dataset.ingredientName.toLowerCase();
    const nameB = orbB.dataset.ingredientName.toLowerCase();

    return incompatibleA.includes(nameB) || incompatibleB.includes(nameA);
  }

  selectIngredient(orb, id, name) {
    const color = getComputedStyle(orb).getPropertyValue('--orb-color').trim();
    const emoji = orb.querySelector('.magnetic-ingredients__orb-icon')?.textContent || '•';

    this.selectedIngredients.push({ id, name, color, emoji });

    this.log('Selected ingredients:', this.selectedIngredients.map(i => i.name));

    // Keep only last 2
    if (this.selectedIngredients.length > 2) {
      const removed = this.selectedIngredients.shift();
      const removedOrb = this.section.querySelector(`[data-ingredient-id="${removed.id}"]`);
      if (removedOrb) {
        removedOrb.classList.remove('is-selected', 'is-merging', 'is-hidden');
        removedOrb.setAttribute('aria-pressed', 'false');
        removedOrb.removeAttribute('title');
        this.resetOrbPosition(removedOrb);
      }
    }

    // Update orb state. The orb stays on its orbit, glowing and bonded to
    // the core; it only flies in during the two-ingredient merge.
    orb.classList.add('is-selected');
    orb.setAttribute('aria-pressed', 'true');
    orb.setAttribute('title', `Click to deselect ${name}`);

    // Update center text to show progress
    this.updateCenterText();

    // Update UI
    this.updateSelectedUI();
    this.updateDisabledOrbs();
    this.updateClearButton();
    this.center.classList.add('is-active');
    this.updateFlask();
    requestAnimationFrame(() => this.renderBonds());

    // Check for product match when 2 selected
    if (this.selectedIngredients.length === 2) {
      this.triggerMergeAnimation();
    } else {
      this.hideProductCard();
    }
  }

  updateCenterText() {
    if (!this.centerText) return;

    if (this.selectedIngredients.length === 0) {
      this.centerText.textContent = this.initialCenterText;
    } else if (this.selectedIngredients.length === 1) {
      this.centerText.textContent = '1 of 2 selected';
    } else {
      this.centerText.textContent = '';
    }
  }

  triggerMergeAnimation() {
    const selectedOrbs = this.selectedIngredients.map(ing =>
      this.section.querySelector(`[data-ingredient-id="${ing.id}"]`)
    ).filter(Boolean);

    this.center.classList.add('is-merging');

    selectedOrbs.forEach(orb => {
      orb.classList.add('is-merging');
      this.animateOrbToCenter(orb);
    });

    setTimeout(() => {
      this.createParticles();

      selectedOrbs.forEach(orb => {
        orb.classList.add('is-hidden');
        orb.classList.remove('is-merging');
      });
      this.renderBonds();

      setTimeout(() => {
        this.center.classList.remove('is-merging');
      }, 300);

      setTimeout(() => {
        this.checkForProductMatch();
      }, 200);
    }, 600);
  }

  deselectIngredient(id) {
    this.selectedIngredients = this.selectedIngredients.filter(i => i.id !== id);

    const orb = this.section.querySelector(`[data-ingredient-id="${id}"]`);
    if (orb) {
      orb.classList.remove('is-selected', 'is-merging', 'is-hidden');
      orb.setAttribute('aria-pressed', 'false');
      orb.removeAttribute('title');
      this.resetOrbPosition(orb);
    }

    // Also reset any other selected orbs that might be hidden
    this.selectedIngredients.forEach(ing => {
      const selectedOrb = this.section.querySelector(`[data-ingredient-id="${ing.id}"]`);
      if (selectedOrb) {
        selectedOrb.classList.remove('is-hidden', 'is-merging');
        this.resetOrbPosition(selectedOrb);
      }
    });

    this.updateCenterText();
    this.updateSelectedUI();
    this.updateDisabledOrbs();
    this.updateClearButton();
    this.hideProductCard();
    this.hideNote();
    this.updateFlask();
    requestAnimationFrame(() => this.renderBonds());

    if (this.selectedIngredients.length === 0) {
      this.center.classList.remove('is-active', 'has-match', 'is-merging');
    }
  }

  animateOrbToCenter(orb) {
    // The orb is anchored with its top-left at the grid center (top/left 50%),
    // so centering it on the core is a plain -50%/-50% translate; the side
    // offset keeps the two merging orbs distinguishable for a beat.
    const selectedIndex = this.selectedIngredients.findIndex(i => i.id === orb.dataset.ingredientId);
    const offsetX = selectedIndex <= 0 ? -35 : 35;
    const transform = `translate(calc(-50% + ${offsetX}px), -50%)`;

    orb.dataset.originalTransform = orb.style.transform || '';
    orb.style.transform = `${transform} scale(0.6)`;
    // the merge pulse keyframes scale around this transform
    orb.style.setProperty('--current-transform', transform);
  }

  resetOrbPosition(orb) {
    orb.style.transform = orb.dataset.originalTransform || '';
  }

  triggerRepel(orb) {
    orb.classList.add('is-repelling');
    this.repellingOrb = orb;

    setTimeout(() => {
      orb.classList.remove('is-repelling');
      this.repellingOrb = null;
    }, 600);
  }

  showIncompatibilityNote(ingredient1, ingredient2) {
    const note = this.compatibilityNotes.find(n => {
      const ingredients = n.ingredients.map(i => i.toLowerCase().trim());
      return (
        ingredients.includes(ingredient1.toLowerCase()) && ingredients.includes(ingredient2.toLowerCase())
      );
    });

    if (note && this.noteContainer) {
      this.noteContainer.innerHTML = note.note;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible';
      this.noteContainer.classList.add(note.isWarning ? 'is-warning' : 'is-success');
    } else if (this.noteContainer) {
      this.noteContainer.innerHTML = `⚠️ ${ingredient1} and ${ingredient2} shouldn't be used together. They may cause irritation or reduce effectiveness.`;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible is-warning';
    }

    // Suggest compatible alternatives
    this.appendCompatibleSuggestion(ingredient2);

    // Auto-hide after a delay
    setTimeout(() => {
      if (this.selectedIngredients.length < 2) {
        this.hideNote();
      }
    }, 5000);
  }

  appendCompatibleSuggestion(rejectedName) {
    if (!this.noteContainer || this.selectedIngredients.length === 0) return;

    const lastSelected = this.selectedIngredients[this.selectedIngredients.length - 1];
    const lastOrb = this.section.querySelector(`[data-ingredient-id="${lastSelected.id}"]`);
    const suggestions = [];

    this.orbs.forEach(orb => {
      const isSelected = this.selectedIngredients.some(i => i.id === orb.dataset.ingredientId);
      const isRejected = orb.dataset.ingredientName === rejectedName;
      if (!isSelected && !isRejected && !this.isIncompatible(lastOrb, orb)) {
        suggestions.push(orb.dataset.ingredientName);
      }
    });

    if (suggestions.length > 0) {
      const suggestionText = suggestions.slice(0, 2).join(' or ');
      this.noteContainer.innerHTML += `<br><small>Try pairing with <strong>${suggestionText}</strong> instead.</small>`;
    }
  }

  hideNote() {
    if (this.noteContainer) {
      this.noteContainer.classList.remove('is-visible');
    }
  }

  updateSelectedUI() {
    if (!this.selectedItemsContainer) return;

    if (this.selectedIngredients.length === 0) {
      this.selectedItemsContainer.innerHTML = `
        <span class="magnetic-ingredients__selected-placeholder">
          ${this.section.querySelector('.magnetic-ingredients__selected-placeholder')?.textContent || 'Click ingredients to select...'}
        </span>
      `;
    } else {
      this.selectedItemsContainer.innerHTML = this.selectedIngredients.map(ing => `
        <div class="magnetic-ingredients__selected-tag" style="--tag-color: ${ing.color}">
          <span class="magnetic-ingredients__selected-tag-icon">${ing.emoji}</span>
          ${ing.name}
        </div>
      `).join('');
    }
  }

  updateDisabledOrbs() {
    if (this.selectedIngredients.length === 0) {
      this.orbs.forEach(orb => {
        orb.classList.remove('is-disabled', 'is-faded', 'is-compatible');
      });
      return;
    }

    const lastSelected = this.selectedIngredients[this.selectedIngredients.length - 1];
    const lastOrb = this.section.querySelector(`[data-ingredient-id="${lastSelected.id}"]`);

    this.orbs.forEach(orb => {
      const isSelected = this.selectedIngredients.some(i => i.id === orb.dataset.ingredientId);

      if (isSelected) {
        orb.classList.remove('is-disabled', 'is-faded', 'is-compatible');
        return;
      }

      if (this.isIncompatible(lastOrb, orb)) {
        orb.classList.add('is-faded');
        orb.classList.remove('is-disabled', 'is-compatible');
      } else {
        orb.classList.add('is-compatible');
        orb.classList.remove('is-disabled', 'is-faded');
      }
    });
  }

  updateClearButton() {
    if (!this.clearBtn) return;

    if (this.selectedIngredients.length > 0) {
      this.clearBtn.classList.add('is-visible');
    } else {
      this.clearBtn.classList.remove('is-visible');
    }
  }

  clearSelection() {
    this.orbs.forEach(orb => {
      orb.classList.remove('is-selected', 'is-disabled', 'is-faded', 'is-merging', 'is-hidden', 'is-compatible');
      orb.setAttribute('aria-pressed', 'false');
      orb.removeAttribute('title');
      this.resetOrbPosition(orb);
    });

    this.selectedIngredients = [];

    this.updateCenterText();
    this.updateSelectedUI();
    this.updateClearButton();
    this.hideProductCard();
    this.hideNote();
    this.updateFlask();
    if (this.bondsSvg) this.bondsSvg.innerHTML = '';
    this.center.classList.remove('is-active', 'has-match', 'is-merging');

    // Return focus to first orb for keyboard users
    const firstOrb = this.section.querySelector('[data-orb]');
    if (firstOrb) {
      firstOrb.focus();
    }
  }

  createParticles() {
    if (!this.particlesContainer) return;

    const particleCount = 16;
    this.particlesContainer.innerHTML = '';

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'magnetic-ingredients__particle';

      const angle = (i * (360 / particleCount)) * (Math.PI / 180);
      const distance = 100 + Math.random() * 60;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;

      particle.style.setProperty('--tx', `${tx}px`);
      particle.style.setProperty('--ty', `${ty}px`);
      particle.style.animationDelay = `${Math.random() * 0.1}s`;

      this.particlesContainer.appendChild(particle);
    }

    setTimeout(() => {
      this.particlesContainer.innerHTML = '';
    }, 1000);
  }

  normalizeIngredientName(name) {
    return name.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  checkForProductMatch() {
    const selectedNames = this.selectedIngredients.map(i => this.normalizeIngredientName(i.name));

    this.log('Checking for product match...', selectedNames);

    const match = this.productMatches.find(pm => {
      if (!pm.ingredients || !Array.isArray(pm.ingredients)) return false;

      const matchIngredients = pm.ingredients.map(i => this.normalizeIngredientName(i));

      const isMatch = (
        matchIngredients.length === selectedNames.length &&
        matchIngredients.every(mi => selectedNames.includes(mi)) &&
        selectedNames.every(si => matchIngredients.includes(si))
      );

      if (isMatch) this.log('Match found!', pm);
      return isMatch;
    });

    if (match) {
      this.showProductCard(match);
      this.showMatchNote(match);
      this.center.classList.add('has-match');
    } else {
      this.log('No exact match found, showing generic');
      this.showGenericMatch();
    }
  }

  showProductCard(match) {
    if (!this.productInner || !this.productCard) return;

    const product = match.product;
    if (!product) return;

    const hasImage = product.image && product.image !== 'null' && product.image !== null;
    const addToCartText = this.section.dataset.addToCartText || 'Add to Cart';
    const isSoldOut = product.available === false;
    const hasVariant = !!product.variantId;
    const hasProductUrl = product.url && product.url !== '#';

    this.log('Showing product card:', product);

    let buttonHtml;
    if (isSoldOut) {
      buttonHtml = `
        <button class="magnetic-ingredients__product-btn" disabled>Sold Out</button>
        ${hasProductUrl
          ? `<a href="${product.url}" class="magnetic-ingredients__product-link">View product for restock notifications →</a>`
          : ''
        }
      `;
    } else {
      buttonHtml = `
        <button
          class="magnetic-ingredients__product-btn"
          data-add-to-cart
          data-variant-id="${product.variantId || ''}"
          data-product-url="${product.url || '#'}"
          ${!hasVariant ? 'disabled' : ''}
        >
          ${addToCartText}
          <svg class="magnetic-ingredients__btn-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 64 64" fill="currentColor">
            <path d="M12,56.43,36.48,32,12,7.52,19.53,0,51.61,32,19.53,64Z"></path>
          </svg>
        </button>
      `;
    }

    this.productInner.innerHTML = `
      <button class="magnetic-ingredients__product-close" data-close-product aria-label="Close product card">✕</button>
      <div class="magnetic-ingredients__product-image-wrap">
        ${hasImage
          ? `<img src="${product.image}" alt="${product.title}" class="magnetic-ingredients__product-image" loading="lazy">`
          : `<span class="magnetic-ingredients__product-emoji">🧴</span>`
        }
      </div>
      <h4 class="magnetic-ingredients__product-title">${product.title || 'Recommended Product'}</h4>
      <p class="magnetic-ingredients__product-benefit">${match.benefit || ''}</p>
      <p class="magnetic-ingredients__product-price${product.compareAtPrice ? ' is-sale' : ''}">
        ${product.price || ''}
        ${product.compareAtPrice
          ? `<span class="price-compare">${product.compareAtPrice}</span><span class="magnetic-ingredients__product-discount" aria-label="${product.discountPercent}% off">-${product.discountPercent}%</span>`
          : ''
        }
      </p>
      ${buttonHtml}
      ${hasProductUrl
        ? `<a href="${product.url}" class="magnetic-ingredients__product-view-link">View full product details</a>`
        : ''
      }
    `;

    this.productCard.classList.add('is-visible');

    // Focus management: move focus to close button after animation
    const closeBtn = this.productInner.querySelector('[data-close-product]');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 550);
    }
  }

  showGenericMatch() {
    if (this.noteContainer) {
      const names = this.selectedIngredients.map(i => i.name).join(' + ');
      this.noteContainer.innerHTML = `💡 ${names}: An interesting combination! These can work well together. Try selecting different ingredients to discover our curated product matches.`;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible is-success';
    }
    this.center.classList.add('has-match');

    // Show the orbs again so the user can try a different combo
    this.selectedIngredients.forEach(ing => {
      const orb = this.section.querySelector(`[data-ingredient-id="${ing.id}"]`);
      if (orb) {
        orb.classList.remove('is-hidden');
      }
    });
  }

  showMatchNote(match) {
    if (!this.noteContainer) return;

    if (match.note) {
      this.noteContainer.innerHTML = match.note;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible';
      this.noteContainer.classList.add(match.isWarning ? 'is-warning' : 'is-success');
    }
  }

  hideProductCard() {
    if (this.productCard) {
      this.productCard.classList.remove('is-visible');
    }
    this.center.classList.remove('has-match');
  }

  async handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const productUrl = button.dataset.productUrl;

    if (!variantId) {
      if (productUrl && productUrl !== '#') {
        window.location.href = productUrl;
      }
      return;
    }

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `
      <span class="magnetic-ingredients__btn-loading"></span>
      Adding...
    `;

    try {
      // Announces the add with Shopify's CartLinesUpdateEvent, so Horizon's
      // cart bubble updates and the cart drawer morphs + auto-opens.
      await ixAddToCart(parseInt(variantId, 10), 1, 'ingredient-lab');

      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        Added!
      `;

      // No drawer on this page (cart type "page"): confirm inline instead
      if (!document.querySelector('cart-drawer')) {
        this.showCartFallbackNotification();
      }

      setTimeout(() => {
        button.disabled = false;
        button.innerHTML = originalText;
      }, 2000);
    } catch (error) {
      console.error('Add to cart error:', error);

      button.innerHTML = `Error - Try Again`;

      setTimeout(() => {
        button.disabled = false;
        button.innerHTML = originalText;
      }, 2000);
    }
  }

  showCartFallbackNotification() {
    const btn = this.productInner?.querySelector('[data-add-to-cart]');
    if (!btn) return;

    // Remove any existing notice
    const existing = this.productInner.querySelector('.magnetic-ingredients__cart-notice');
    if (existing) existing.remove();

    const notice = document.createElement('p');
    notice.className = 'magnetic-ingredients__cart-notice';
    notice.textContent = 'Item added! View your cart to check out.';
    notice.setAttribute('role', 'status');
    btn.insertAdjacentElement('afterend', notice);

    setTimeout(() => notice.remove(), 4000);
  }

}

// Initialize all sections
function initMagneticIngredients() {
  document.querySelectorAll('.magnetic-ingredients').forEach(section => {
    if (!section.dataset.initialized) {
      new MagneticIngredients(section);
      section.dataset.initialized = 'true';
    }
  });
}

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMagneticIngredients);
} else {
  initMagneticIngredients();
}

// Re-init on Shopify section events (for theme editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('.magnetic-ingredients')) {
    initMagneticIngredients();
  }
});

document.addEventListener('shopify:section:reorder', initMagneticIngredients);
